/**
 * Frontend mirror of the backend secret-redaction backstop
 * (`common/infrastructure/observability/secret_redaction.py`), applied to
 * everything the Sentry SDK sends off-site: error events (including Vue
 * component props attached by the SDK), transactions, and breadcrumbs.
 *
 * Best-effort denylist, NOT the primary control. It reliably redacts the
 * credential field names and token shapes it knows about, but cannot catch a
 * secret under a brand-new field name or in an unknown token format. The
 * primary control remains keeping secrets out of capture payloads in the
 * first place — never pass form/config objects to `captureError`'s `data`.
 */

export const REDACTED = '<redacted>';

// Canonical snake_case names, kept in sync with the backend policy:
// GENERIC_SENSITIVE_FIELD_NAMES plus every key declared in
// common/domain/integration_secret_keys.py. Key matching is done on a
// normalized form (lowercase, separators stripped), so camelCase variants
// such as `apiKey`/`clientSecret` are covered by the same entries.
const SENSITIVE_KEY_NAMES: readonly string[] = [
  'api_key',
  'api_token',
  'app_key',
  'access_token',
  'refresh_token',
  'access_key_id',
  'secret_access_key',
  'secret_key',
  'public_key',
  'client_secret',
  'token',
  'password',
  'authorization',
  'private_key',
  'secret',
  'credential',
  'credentials',
  'pat',
  'headers',
  'account_key',
  'sas_token',
  'connection_string',
  'dd_api_key',
  'dd_application_key',
  'compliance_access_key',
  'analytics_api_key',
];

const normalizeKey = (key: string): string => key.toLowerCase().replace(/[^a-z0-9]/g, '');

const SENSITIVE_KEYS: ReadonlySet<string> = new Set(SENSITIVE_KEY_NAMES.map(normalizeKey));

const isSensitiveKey = (key: string): boolean => SENSITIVE_KEYS.has(normalizeKey(key));

const toCamelCase = (name: string): string =>
  name.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase());

const SENSITIVE_KEY_SPELLINGS = [
  ...new Set(SENSITIVE_KEY_NAMES.flatMap((name) => [name, toCamelCase(name)])),
];

// Redacts `key=value` / `key: value` / `"key": "value"` forms embedded in a
// string (error messages, breadcrumb text, URLs), built from the same name
// list so there is one source of truth.
const KEY_VALUE_RE = new RegExp(
  String.raw`(\b(?:${SENSITIVE_KEY_SPELLINGS.join('|')})\b['"]?\s*[:=]\s*)('[^']*'|"[^"]*"|[^\s,;'"})&#]+)`,
  'gi',
);

// High-precision patterns for secrets embedded inside a string, mirroring the
// backend's SECRET_VALUE_PATTERNS, plus frontend-specific shapes: session JWTs
// and the OAuth authorization code carried on the connector callback URL.
const SECRET_VALUE_RULES: ReadonlyArray<{ pattern: RegExp; replacement: string }> = [
  {
    pattern: /\b(?:bearer|basic|digest|negotiate|ntlm|token|apikey)\s+[A-Za-z0-9+/=._~-]{8,}/gi,
    replacement: REDACTED,
  },
  { pattern: /\bgh[posur]_[A-Za-z0-9]{20,}/g, replacement: REDACTED },
  { pattern: /\bgithub_pat_[A-Za-z0-9_]{20,}/g, replacement: REDACTED },
  { pattern: /\bxox[baprs]-[A-Za-z0-9-]{10,}/g, replacement: REDACTED },
  { pattern: /\bAKIA[0-9A-Z]{16}\b/g, replacement: REDACTED },
  { pattern: /\bya29\.[A-Za-z0-9_-]{20,}/g, replacement: REDACTED },
  { pattern: /\bAT(?:ATT3|CTT3)[A-Za-z0-9_=-]{20,}/g, replacement: REDACTED },
  { pattern: /\bsig=[A-Za-z0-9%+/=_-]{10,}/gi, replacement: REDACTED },
  {
    // The lookbehind only lets a match start at the beginning of a base64url run,
    // so a run of near-miss `eyJ...-eyJ...` candidates is scanned once, not once per `eyJ`.
    pattern: /(?<![A-Za-z0-9_-])eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g,
    replacement: REDACTED,
  },
  { pattern: /([?&]code=)[^&#\s]+/gi, replacement: `$1${REDACTED}` },
  { pattern: KEY_VALUE_RE, replacement: `$1${REDACTED}` },
];

export function scrubText(text: string): string {
  return SECRET_VALUE_RULES.reduce(
    (result, rule) => result.replace(rule.pattern, rule.replacement),
    text,
  );
}

// Only plain objects may be walked and mutated. Host objects have live
// accessors — assigning `location.href` navigates the page, `location.origin`
// throws — and class instances are live app state, not captured payload data.
function isPlainObject(value: object): value is Record<string, unknown> {
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function redactInPlace(value: unknown, seen: WeakSet<object>): void {
  if (typeof value !== 'object' || value === null || seen.has(value)) return;
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (typeof item === 'string') value[index] = scrubText(item);
      else redactInPlace(item, seen);
    });
    return;
  }

  if (!isPlainObject(value)) return;

  for (const [key, entry] of Object.entries(value)) {
    if (isSensitiveKey(key) && entry != null && entry !== '') value[key] = REDACTED;
    else if (typeof entry === 'string') value[key] = scrubText(entry);
    else redactInPlace(entry, seen);
  }
}

/**
 * Deep-redacts credentials in place and returns the same reference, so it can
 * be used directly as Sentry's `beforeSend` / `beforeSendTransaction` /
 * `beforeBreadcrumb` hook body.
 */
export function redactSensitive<T>(value: T): T {
  redactInPlace(value, new WeakSet());
  return value;
}
